import React, { useState } from 'react'
import { Icon } from 'semantic-ui-react'
import InlineTooltip from './InlineTooltip'

export const DEFAULT_ICON_BUTTON_STYLE = {
  textDecoration: 'none',
  background: 'transparent',
  border: 'none',
  fontSize: '1.2em'
}


const DEFAULT_HOVER_ICON_STYLE = { textDecoration: 'none' }

export default function IconButton(
  {
    iconName,
    ariaLabel,
    tooltip,

    tooltipDelayMs = 700,
    // boolean OR (hovered:boolean) => boolean
    tooltipVisible,

    iconSize,

    buttonClassName = 'btn btn-link btn-sm p-0',
    buttonStyle,
    wrapperStyle,

    idleIconStyle = { opacity: 0.4, filter: 'brightness(0.7)', textDecoration: 'none' },
    hoverIconStyle = DEFAULT_HOVER_ICON_STYLE,

    preventDefault = false,
    stopPropagation = false,
    disabled = false,

    onClick,
    onMouseEnter,
    onMouseLeave,
    onFocus,
    onBlur,

    // allow native browser tooltip when explicitly requested
    title,

    // pass-through props (e.g. react-table prop getters)
    buttonProps = {}
  }
) {
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)

  const resolvedTooltipVisible = (() => {
    if (typeof tooltipVisible === 'function') return tooltipVisible(hovered)
    if (tooltipVisible !== undefined) return Boolean(tooltipVisible)
    return hovered
  })()

  const {
    onClick: buttonPropsOnClick,
    onMouseEnter: buttonPropsOnMouseEnter,
    onMouseLeave: buttonPropsOnMouseLeave,
    onFocus: buttonPropsOnFocus,
    onBlur: buttonPropsOnBlur,
    className: buttonPropsClassName,
    style: buttonPropsStyle,
    ...restButtonProps
  } = buttonProps || {}

  const handleClick = (e) => {
    if (preventDefault) e.preventDefault()
    if (stopPropagation) e.stopPropagation()
    if (disabled) return

    buttonPropsOnClick?.(e)
    onClick?.(e)
  }

  const handleMouseEnter = (e) => {
    setHovered(true)
    buttonPropsOnMouseEnter?.(e)
    onMouseEnter?.(e)
  }

  const handleMouseLeave = (e) => {
    setHovered(false)
    buttonPropsOnMouseLeave?.(e)
    onMouseLeave?.(e)
  }

  const handleFocus = (e) => {
    setFocused(true)
    buttonPropsOnFocus?.(e)
    onFocus?.(e)
  }

  const handleBlur = (e) => {
    setFocused(false)
    buttonPropsOnBlur?.(e)
    onBlur?.(e)
  }

  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-block',
        ...(wrapperStyle || {}),
      }}
    >
      <button
        type='button'
        className={[
          'icon-button',
          buttonClassName,
          buttonPropsClassName,
        ].filter(Boolean).join(' ')}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        style={{
          ...DEFAULT_ICON_BUTTON_STYLE,
          ...(buttonPropsStyle || {}),
          ...(buttonStyle || {}),
        }}
        {...restButtonProps}
        title={title}  // if undefined, suppress tooltip; if string, show native tooltip
      >
        <Icon
          name={iconName}
          size={iconSize}
          style={hovered ? hoverIconStyle : idleIconStyle}
        />
      </button>

      {tooltip ? (
        <InlineTooltip visible={resolvedTooltipVisible} delayMs={tooltipDelayMs}>
          {tooltip}
        </InlineTooltip>
      ) : null}
    </span>
  )
}
